import SignalCellularOffIcon from '@mui/icons-material/SignalCellularOff';
import { Alert, Collapse, FormControlLabel, Grid, Stack, Typography } from '@mui/material';
import { get } from 'lodash';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useChangeCellularMode } from '@/api/device';
import { useSpotterModeChangeStatus } from '@/api/device/useSpotterModeChangeStatus';
import { useDevice, useDeviceSubscriptionStatus } from '@/api/devices';
import { ActionDialog } from '@/components/ActionDialog';
import { Null } from '@/components/Null';
import { FormSelect } from '@/components/form/FormSelect';
import { FormSwitch } from '@/components/form/FormSwitch';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { CellularModeChangeText } from '@/components/spotter/configurationTab/PendingModeChangeAlert';
import { ResetUpdateButtonGroup } from '@/components/userAccount/ResetUpdateButtonGroup';
import {
    cellularDeploymentConversionMap,
    cellularDeploymentTypeOptions,
    cellularProcessingRateOptions,
} from '@/config';
import { useDisclosure } from '@/hooks/useDisclosure';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';

type CellularConfigFormValues = {
    deploymentType: string;
    processingRateMins: string;
    hdrEnabled: boolean;
};

const formDefaultValues: CellularConfigFormValues = {
    deploymentType: '',
    processingRateMins: '',
    hdrEnabled: false,
};

export const CellularDataSection = ({ readOnly }: { readOnly?: boolean }) => {
    const { isOpen: isConfirmDialogOpen, open: openConfirmDialog, close: closeConfirmDialog } = useDisclosure();
    const { mutateAsync: changeCellularMode, isPending: pendingMutation } = useChangeCellularMode();
    const { data: device, isPending: isDevicesPending, isError: isDevicesRequestError } = useDevice();
    const spotterId = device?.spotterId;

    const { data: subscriptionStatus } = useDeviceSubscriptionStatus({ spotterId });
    const { pendingModeSettingGroup, hdrEnabled } = useSpotterModeChangeStatus({ spotterId });

    const {
        control: formControl,
        reset: formReset,
        handleSubmit: handleFormSubmit,
        formState,
        getValues: formGetValues,
    } = useForm<CellularConfigFormValues>({
        defaultValues: formDefaultValues,
        mode: 'onChange',
    });
    useUnsavedChangesGuard(formState.isDirty, 'cellular-data');

    const isTelemetryEnabled = subscriptionStatus?.cellEnabled || subscriptionStatus?.satEnabled;
    const settingsLocked = !!(readOnly || pendingModeSettingGroup || !isTelemetryEnabled);

    const onCellularDataFormReset = () => {
        formReset();
    };

    const onSubmit = () => {
        // if hdr enabled or processing rate are dirty, prompt for confirmation. if it's just deployment type,
        // we can switch it back and forth as much as we want, so just commit it immediately.
        if (formState.dirtyFields.hdrEnabled || formState.dirtyFields.processingRateMins) {
            openConfirmDialog();
        } else {
            onActionConfirm();
        }
    };

    const onActionConfirm = async () => {
        if (!spotterId) {
            // eslint-disable-next-line no-console
            console.log('No Spotter selected');
            return;
        }
        const values = formGetValues();
        const { fMax, fMin } = get(cellularDeploymentConversionMap, values.deploymentType, { fMax: 0, fMin: 0 });
        try {
            await changeCellularMode({
                spotterId,
                fMax,
                fMin,
                processingRateMins: parseInt(values.processingRateMins),
                hdrEnabled: values.hdrEnabled,
            });
        } catch (err) {
            console.error(err);
        }
        onCellularDataFormReset();
        closeConfirmDialog();
    };

    // decomposing fields we care about out of device so the effect that initializes the form doesn't rerun on every device refresh
    const { fMin } = device?.cellularData.cellularSettings ?? {};

    /**
     * We don't want to use device.cellularData.cellularSettings.processingRateMins to populate currentProcessingRate.
     * When a user changes the processing rate, the new value gets reflected in processingRateMins immediately while outboundNoteSyncPeriodMin becomes desynced until Spotter checks in with the new value
     * While that happens, we want to keep displaying the _current_ Spotter rate to maintain consistency with all the other disabled fields except for deployment type (more details below)
     *
     * Pulling this out outside the effect below so it doesn't reset the form on every device refresh
     */
    const currentProcessingRate = device?.cellularData.currentCellularFirmwareSettings.outboundNoteSyncPeriodMin;

    useEffect(() => {
        if (isDevicesPending || !fMin) {
            return;
        }

        /**
         * There's a slightly confusing ui experience here due to some form values having a firmware counterpart and one that doesn't
         *  - hdr enable and processing rate have firmware configs to update that is only updated after the spotter checks in again
         *  - deployment type is a software config that are updated immediately upon clicking update
         *
         * Our current desired behavior for "locking" out users when a mode change is pending is to display the *current* configs in a disabled form.
         * The value users see in the deployment type field in the disabled form is the *new* config since there isn't an additional async component for it
         */
        const currentCellularDeploymentTypeValue = get(cellularDeploymentConversionMap, fMin, { value: '' }).value;

        formReset({
            deploymentType: currentCellularDeploymentTypeValue,
            processingRateMins: `${currentProcessingRate}`,
            hdrEnabled,
        });
    }, [formReset, isDevicesPending, hdrEnabled, currentProcessingRate, fMin]);

    if (device?.currentMode === 'deployment') {
        return <Null data-testid="idle-mode-no-cell-data" />;
    }

    if (isDevicesPending) {
        return (
            <Grid container gap={2} sx={{ my: 2 }}>
                <Grid size={{ xs: 12 }}>
                    <Typography variant="h5">Cellular data</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <GenericSkeleton />
                </Grid>
            </Grid>
        );
    }

    if (isDevicesRequestError) {
        return (
            <Grid container gap={2} sx={{ my: 2 }}>
                <Grid size={{ xs: 12 }}>
                    <Typography variant="h5">Cellular data</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <Alert severity="error" icon={<SignalCellularOffIcon />}>
                        <Typography gutterBottom>The cellular data configuration could not be loaded.</Typography>
                    </Alert>
                </Grid>
            </Grid>
        );
    }

    if (!device?.cellularData.hasCellular) {
        return (
            <Grid container gap={2}>
                <Grid size={{ xs: 12 }}>
                    <Typography variant="h5">Cellular data</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <Alert severity="info" icon={<SignalCellularOffIcon />}>
                        <Typography gutterBottom>This Spotter is not equipped with cellular hardware.</Typography>
                    </Alert>
                </Grid>
            </Grid>
        );
    }

    return (
        <Stack gap={2}>
            <Typography variant="h5">Cellular data</Typography>
            <Grid size={{ xs: 12, sm: 10 }}>
                <FormControlLabel
                    value="start"
                    sx={{
                        ml: 0,
                        width: '100%',
                        justifyContent: 'space-between',
                        gap: 5,
                    }}
                    control={
                        <FormSwitch
                            name="hdrEnabled"
                            control={formControl}
                            switchProps={{
                                readOnly: readOnly,
                                disabled: settingsLocked && !readOnly,
                            }}
                        />
                    }
                    label={
                        <Stack>
                            <Typography>High data rate (HDR)</Typography>
                            <Typography variant="minorLabel">
                                HDR leverages your Spotter&apos;s high bandwidth cellular connection to deliver wave
                                displacement data directly to Sofar&apos;s backend, unlocking powerful and flexible full
                                spectral processing. Once enabled, HDR data will appear next to embedded data in the
                                Spotter Dashboard and APIs.
                            </Typography>
                        </Stack>
                    }
                    labelPlacement="start"
                />
            </Grid>
            <Grid container spacing={4} size={{ xs: 12, sm: 8 }} justifyContent="space-between">
                <Grid size={{ xs: 6, sm: 6 }}>
                    <FormSelect
                        name="deploymentType"
                        control={formControl}
                        label="Deployment type"
                        options={cellularDeploymentTypeOptions}
                        inputFieldProps={{
                            id: 'cellular-data-deployment-ype',
                            type: 'text',
                            size: 'small',
                            disabled: settingsLocked,
                        }}
                        controllerProps={{
                            rules: {
                                required: 'Field is required',
                            },
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 6, sm: 6 }}>
                    <FormSelect
                        name="processingRateMins"
                        control={formControl}
                        label="Processing rate"
                        inputFieldProps={{
                            id: 'cellular-data-processing-rate',
                            type: 'number',
                            size: 'small',
                            disabled: settingsLocked,
                        }}
                        options={cellularProcessingRateOptions}
                        controllerProps={{
                            rules: {
                                required: 'Field is required',
                            },
                        }}
                    />
                </Grid>
            </Grid>
            <Grid size={{ xs: 12 }}>
                <Collapse in={formState.isDirty}>
                    <ResetUpdateButtonGroup
                        onReset={onCellularDataFormReset}
                        onSubmit={handleFormSubmit(onSubmit)}
                        isResetDisabled={!formState.isDirty || formState.isSubmitting}
                        isUpdateDisabled={!formState.isDirty || formState.isSubmitting}
                        isSubmitting={formState.isSubmitting}
                        containerStyle={{ justifyContent: { xs: 'flex-end', sm: 'flex-start' }, mt: 1, mb: 0 }}
                        updateLabel="Update"
                    />
                </Collapse>
            </Grid>
            <ActionDialog
                isOpen={isConfirmDialogOpen}
                isLoading={pendingMutation}
                onReject={closeConfirmDialog}
                onConfirm={onActionConfirm}
                title="Are you sure you want to send these changes to your device?"
                confirmLabel="Yes, send change"
                rejectLabel="No, go back"
                message={[
                    <>
                        This will change your Spotter&apos;s settings to{' '}
                        <CellularModeChangeText
                            currentSettings={device?.cellularData?.currentCellularFirmwareSettings}
                            targetSettings={{
                                outboundNoteSyncPeriodMin: Number(formGetValues().processingRateMins),
                                hdrRecordsPerMessage: formGetValues().hdrEnabled ? 1 : 0,
                            }}
                        />
                        .
                    </>,
                    <>
                        This change may take several hours to complete, during which your Spotter will continue to send
                        data using its current settings. You will not be able to make changes to your Spotter&apos;s
                        data transmission settings until this process is complete.
                    </>,
                ]}
            />
        </Stack>
    );
};
